import { useEffect, useState } from 'react'

import {
  Box,
  Button,
  Card,
  Collapse,
  Group,
  Loader,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconAlertTriangle,
  IconCheck,
  IconInfoCircle,
  IconRefresh,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'

import { usdFormatter } from '@repo/common/helpers/formatters'
import {
  type Fund,
  useAdminFund,
  useAdminFundAssetNavs,
  useApproveFundNav,
} from '@repo/common/queries/admin/fund'

import { TowerUserLink } from '@/components/TowerUserLink'
import { TowerCard } from '@/components/ui/TowerCard'

import { TowerApprovalControl } from '../ui/TowerApprovalControl'

import { AssetNavTable } from './AssetNavTable'

dayjs.extend(relativeTime)
dayjs.extend(utc)

interface NavApprovalsControlProps {
  fundId?: string
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface NoPublicationPanelProps {}

function NoPublicationPanel({}: NoPublicationPanelProps) {
  return (
    <Stack align="center" gap="md">
      <Group>
        <IconInfoCircle size={48} color="gray" />

        <Text size="sm" c="dimmed" ta="center">
          There is currently no NAV publication scheduled
        </Text>
      </Group>
    </Stack>
  )
}

interface ApprovalPanelProps {
  fund: Fund
  overwrite?: boolean
  lockedNavData: LockedNavData | null
  setLockedNavData: (data: LockedNavData | null) => void
}

type LockedNavData = NonNullable<Fund['live_nav']>

interface AssetNavsPanelProps {
  fund: Fund
  effective_at?: string | null
}

function AssetNavsPanel({ fund, effective_at }: AssetNavsPanelProps) {
  const shouldQuery = Boolean(effective_at)

  const { data: assetNavsData, isLoading } = useAdminFundAssetNavs({
    fundId: shouldQuery ? fund.id : '',
    effective_at: effective_at || undefined,
  })

  if (!shouldQuery) {
    return (
      <Stack gap="xs">
        <Text size="sm" c="dimmed">
          Lock price to view asset NAV details
        </Text>
      </Stack>
    )
  }

  if (isLoading) {
    return (
      <Stack gap="xs">
        <Text size="sm" c="dimmed">
          Asset NAVs
        </Text>
        <Skeleton height={200} />
      </Stack>
    )
  }

  return (
    <Stack gap="xs">
      <Text size="sm" c="dimmed">
        Asset NAVs as of {dayjs(effective_at).format('MMM DD, YYYY h:mm A')}
      </Text>
      <AssetNavTable
        data={assetNavsData}
        isLoading={isLoading}
        effective_at={effective_at || undefined}
      />
    </Stack>
  )
}

function ApprovalPanel({
  fund,
  overwrite,
  lockedNavData,
  setLockedNavData,
}: ApprovalPanelProps) {
  const liveNav = fund.live_nav
  const approveNavMutation = useApproveFundNav()

  if (!liveNav) return null

  const displayData = lockedNavData || liveNav
  const currentPrice = displayData.price_per_share

  const handleLockPrice = () => {
    setLockedNavData(liveNav)
  }

  const handleApprove = async () => {
    const effectiveAt = lockedNavData?.effective_at
    if (!effectiveAt) return
    if (approveNavMutation.isPending) return
    await approveNavMutation.mutateAsync({
      fundId: fund.id,
      effectiveAt,
      overwrite,
    })
  }

  const handleComplete = () => {
    setLockedNavData(null)
  }

  return (
    <Stack gap="md">
      <Group gap="lg" align="flex-start">
        <Box>
          <Group>
            <Text size="sm" c="dimmed">
              Effective At
            </Text>
          </Group>

          <Text size="md" c={lockedNavData ? 'blue' : 'dimmed'} fw={500}>
            {dayjs(liveNav.effective_at).format('MMM DD, YYYY h:mm A')}
          </Text>
          {lockedNavData ? (
            <Text size="xs" c="blue">
              Locked for Approval
            </Text>
          ) : (
            <Text size="xs" c="dimmed">
              Latest
            </Text>
          )}
        </Box>

        <Box>
          <Text size="sm" c="dimmed">
            Publishing At
          </Text>
          <Text size="md" fw={500}>
            {dayjs(fund.next_publication_at).format('MMM DD, YYYY h:mm A')}
          </Text>
        </Box>

        <Box>
          <Text size="sm" c="dimmed">
            In
          </Text>
          <Countdown to={fund.next_publication_at || undefined} />
        </Box>
      </Group>

      <Stack gap="md">
        <Group gap="lg" align="flex-start">
          <Box>
            <Text size="sm" c="dimmed">
              Price Per Share
            </Text>
            <Text size="lg" fw={700}>
              {usdFormatter(currentPrice)}
            </Text>
          </Box>

          <Box>
            <Text size="sm" c="dimmed">
              Total Valuation
            </Text>
            <Text size="lg" fw={700}>
              {usdFormatter(displayData.valuation)}
            </Text>
          </Box>
        </Group>

        <Group gap="lg" align="flex-start" wrap="wrap">
          <Box>
            <Text size="sm" c="dimmed">
              Assets Value
            </Text>
            <Text size="sm" fw={500}>
              {usdFormatter(displayData.assets_total_value)}
            </Text>
          </Box>

          <Box>
            <Text size="sm" c="dimmed">
              Cash Value
            </Text>
            <Text size="sm" fw={500}>
              {usdFormatter(displayData.cash_total_value)}
            </Text>
          </Box>

          <Box>
            <Text size="sm" c="dimmed">
              Unminted Deposits
            </Text>
            <Text size="sm" fw={500}>
              {usdFormatter(displayData.cash_pending_value)}
            </Text>
          </Box>

          <Box>
            <Text size="sm" c="dimmed">
              Pending Shares
            </Text>
            <Text size="sm" fw={500}>
              {displayData.pending_shares.toLocaleString() || '0'}
            </Text>
          </Box>
        </Group>
      </Stack>

      <Button
        onClick={
          lockedNavData
            ? () => {
                setLockedNavData(null)
              }
            : handleLockPrice
        }
        variant={lockedNavData ? 'subtle' : 'filled'}
        color="blue"
      >
        {lockedNavData ? 'Unlock Price' : 'Lock Price'}
      </Button>

      <TowerApprovalControl
        onApprove={handleApprove}
        onCompleted={handleComplete}
        disabled={approveNavMutation.isPending || !lockedNavData}
        buttonText={
          overwrite ? 'Overwrite/Approve NAV' : 'Approve NAV Publication'
        }
        successText="NAV approved successfully"
      />
    </Stack>
  )
}

interface ExistingApprovedPanelProps {
  fund: Fund
  lockedNavData: LockedNavData | null
  setLockedNavData: (data: LockedNavData | null) => void
  onRefresh?: () => void
}

function NavCard({ children }: { children: React.ReactNode }) {
  return (
    <Card
      withBorder
      p="lg"
      radius="md"
      bg="light-dark(var(--mantine-color-gray-1),var(--mantine-color-dark-7))"
    >
      {children}
    </Card>
  )
}

function Countdown({ to }: { to?: string }) {
  const [now, setNow] = useState(dayjs.utc())

  const target = dayjs.utc(to)
  const diff = target.diff(now)
  const hours = target.diff(now, 'hour')
  const minutes = target.subtract(hours, 'hour').diff(now, 'minute')
  const seconds = target
    .subtract(hours, 'hour')
    .subtract(minutes, 'minute')
    .diff(now, 'second')
  const days = Math.floor(hours / 24)
  const color = diff < 60_000 ? (diff < 10_000 ? 'red' : 'orange') : ''
  const size = diff < 60_000 ? 'lg' : 'md'

  useEffect(() => {
    const iid = setInterval(() => {
      setNow(dayjs.utc())
    }, 1000)
    return () => {
      clearInterval(iid)
    }
  }, [])

  if (!to) return <Text />

  if (diff <= 0) {
    return <Text />
  }

  return (
    <Text size={size} fw={500} c={color}>
      {days > 0 && `${days}d `}
      {(days > 0 || hours > 0) && `${hours % 24}h`} &nbsp;
      {(days > 0 || hours > 0 || minutes > 0) &&
        `${minutes}m`.padStart(3, '0')}{' '}
      &nbsp;
      {days == 0 && hours == 0 && `${seconds}s`.padStart(3, '0')}
    </Text>
  )
}

function ExistingApprovedPanel({
  fund,
  lockedNavData,
  setLockedNavData,
  onRefresh,
}: ExistingApprovedPanelProps) {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const liveNav = fund.live_nav
  const nav = fund.upcoming_nav

  const handleRefresh = () => {
    if (isRefreshing || !onRefresh) return

    setIsRefreshing(true)
    onRefresh()

    setTimeout(() => {
      setIsRefreshing(false)
    }, 2000)
  }

  const hasDelta =
    liveNav &&
    nav &&
    (liveNav.valuation !== nav.valuation ||
      liveNav.price_per_share !== nav.price_per_share ||
      liveNav.assets_total_value !== nav.assets_total_value ||
      liveNav.cash_total_value !== nav.cash_total_value)
  const [overwriteOpen, overwriteOpenHandlers] = useDisclosure(false)
  if (!nav) return null
  const navDeltaPositive = liveNav && liveNav.valuation - nav.valuation > 0
  return (
    <Stack gap="md">
      <Group gap="lg" align="flex-start">
        <Box>
          <Text size="sm" c="dimmed">
            Effective At
          </Text>
          <Text size="md" c="blue" fw={500}>
            {dayjs(nav.effective_at).format('MMM DD, YYYY h:mm A')}
          </Text>
        </Box>

        <Box>
          <Text size="sm" c="dimmed">
            Publishing At
          </Text>
          <Text size="md" fw={500}>
            {dayjs(nav.published_at).format('MMM DD, YYYY h:mm A')}
          </Text>
        </Box>

        <Box>
          <Text size="sm" c="dimmed">
            In
          </Text>
          <Countdown to={fund.next_publication_at || undefined} />
        </Box>
      </Group>

      <Stack gap="md">
        <Group gap="lg" align="flex-start">
          <Box>
            <Text size="sm" c="dimmed">
              Price Per Share
            </Text>
            <Text size="lg" fw={700}>
              {usdFormatter(nav.price_per_share)}
            </Text>
          </Box>

          <Box>
            <Text size="sm" c="dimmed">
              Total Valuation
            </Text>
            <Text size="lg" fw={700}>
              {usdFormatter(nav.valuation)}
            </Text>
          </Box>
        </Group>

        <Group gap="lg" align="flex-start" wrap="wrap">
          <Box>
            <Text size="sm" c="dimmed">
              Assets Value
            </Text>
            <Text size="sm" fw={500}>
              {usdFormatter(nav.assets_total_value)}
            </Text>
          </Box>

          <Box>
            <Text size="sm" c="dimmed">
              Cash Value
            </Text>
            <Text size="sm" fw={500}>
              {usdFormatter(nav.cash_total_value)}
            </Text>
          </Box>

          <Box>
            <Text size="sm" c="dimmed">
              Unminted Deposits
            </Text>
            <Text size="sm" fw={500}>
              {usdFormatter(nav.cash_pending_value)}
            </Text>
          </Box>

          <Box>
            <Text size="sm" c="dimmed">
              Unminted Shares
            </Text>
            <Text size="sm" fw={500}>
              {nav.pending_shares.toLocaleString() || '0'}
            </Text>
          </Box>
        </Group>
      </Stack>

      <Group>
        <Box>
          <Text size="sm" c="dimmed">
            Approved By
          </Text>
          {nav.published_by_id && (
            <TowerUserLink userId={nav.published_by_id} displayId={false} />
          )}
          {!nav.published_by_id && 'Auto-Approved'}
        </Box>
      </Group>

      <NavCard>
        <Stack>
          {!hasDelta && (
            <Group>
              <IconCheck color="green" />
              <Text size="sm" c="dimmed">
                This is the most up-to-date NAV data.
              </Text>
            </Group>
          )}

          {hasDelta && (
            <Group>
              <IconAlertTriangle color="orange" />

              <Text size="sm" c="dimmed">
                Newer NAV data is available that can overwrite this upcoming
                publication.
              </Text>
            </Group>
          )}

          {hasDelta && (
            <Group>
              <Box>
                <Text size="sm" c="dimmed">
                  &nbsp;
                </Text>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={handleRefresh}
                  disabled={isRefreshing || !!lockedNavData}
                >
                  <IconRefresh />
                </Button>
              </Box>

              <Box>
                <Text size="sm" c="dimmed">
                  Delta
                </Text>
                <Text size="md" fw={500} c="orange">
                  {navDeltaPositive && '+'}
                  {`${(((liveNav.valuation - nav.valuation) / nav.valuation) * 100).toFixed(2)}%`}
                </Text>
              </Box>

              <Box>
                <Text size="sm" c="dimmed">
                  Valuation
                </Text>
                <Text size="md" fw={500} c="orange">
                  {navDeltaPositive && '+'}
                  {usdFormatter(liveNav.valuation - nav.valuation)}
                </Text>
              </Box>

              <Box>
                <Text size="sm" c="dimmed">
                  PPS
                </Text>
                <Text size="md" fw={500} c="orange">
                  {navDeltaPositive && '+'}
                  {usdFormatter(liveNav.price_per_share - nav.price_per_share)}
                </Text>
              </Box>
            </Group>
          )}

          {hasDelta && !overwriteOpen && (
            <Button variant="subtle" onClick={overwriteOpenHandlers.toggle}>
              Overwrite...
            </Button>
          )}

          {hasDelta && (
            <Collapse in={overwriteOpen}>
              <ApprovalPanel
                fund={fund}
                overwrite={true}
                lockedNavData={lockedNavData}
                setLockedNavData={setLockedNavData}
              />
            </Collapse>
          )}
        </Stack>
      </NavCard>
    </Stack>
  )
}

export function NavApprovalsControl({ fundId }: NavApprovalsControlProps) {
  const {
    data: fund,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useAdminFund(fundId || '', 2 * 60 * 1000) // regular refresh interval

  const [lockedNavData, setLockedNavData] = useState<LockedNavData | null>(null)

  const getDisplayMode = () => {
    if (!fund) return 'no_publication'
    if (fund.next_publication_at == null) return 'no_publication'
    if (fund.upcoming_nav == null) return 'awaiting_approval'
    return 'existing_approved'
  }

  const displayMode = getDisplayMode()

  if (isLoading) {
    return (
      <TowerCard
        title="NAV Publication Control"
        description="Loading"
        icon={<IconCheck />}
      >
        <Skeleton height={60} />
      </TowerCard>
    )
  }

  if (error || !fund) {
    return (
      <TowerCard
        title="NAV Publication Control"
        description="Manage and approve upcoming NAV publications"
        icon={<IconCheck />}
      >
        <Stack align="center" gap="md">
          <IconInfoCircle size={48} color="red" />
          <Text size="lg" fw={500} ta="center" c="red">
            Error loading fund data
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            {error
              ? 'Failed to load fund information. Please try again.'
              : 'Fund data not found.'}
          </Text>
        </Stack>
      </TowerCard>
    )
  }

  const modeTitle = {
    no_publication: 'No Upcoming Publication',
    existing_approved: 'Approved',
    awaiting_approval: 'Awaiting Approval',
  }[displayMode]
  const title = `NAV Publication: ${modeTitle}`
  const officialEstimated = fund.next_publication_official ? (
    <Text size="lg" fw={600} c="orange">
      Official
    </Text>
  ) : (
    <Text size="lg" fw={600}>
      Estimated
    </Text>
  )
  const titleElement = (
    <Group gap="xs" align="center" wrap="nowrap">
      <Text size="lg" fw={700}>
        {title}:
      </Text>
      {officialEstimated}
      <Loader
        type="bars"
        size="xs"
        style={{ opacity: isFetching ? 1 : 0, transition: 'opacity .2s ease' }}
      />
    </Group>
  )
  const getAssetNavEffectiveAt = () => {
    if (displayMode === 'awaiting_approval' && lockedNavData) {
      return lockedNavData.effective_at
    }
    if (displayMode === 'existing_approved' && fund.upcoming_nav) {
      return fund.upcoming_nav.effective_at
    }
    return null
  }

  return (
    <TowerCard
      title={titleElement}
      description="Manage and approve upcoming NAV publications"
      icon={<IconCheck />}
    >
      <Stack gap="xl">
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {displayMode === 'no_publication' && <NoPublicationPanel />}

          {displayMode === 'awaiting_approval' && (
            <ApprovalPanel
              fund={fund}
              lockedNavData={lockedNavData}
              setLockedNavData={setLockedNavData}
            />
          )}

          {displayMode === 'existing_approved' && (
            <ExistingApprovedPanel
              fund={fund}
              lockedNavData={lockedNavData}
              setLockedNavData={setLockedNavData}
              onRefresh={() => void refetch()}
            />
          )}

          <AssetNavsPanel fund={fund} effective_at={getAssetNavEffectiveAt()} />
        </SimpleGrid>
      </Stack>
    </TowerCard>
  )
}
